'use client';

import { useRTE } from '@pickleballinc/ui/components/global/RTEProvider';
import type { RTEMatchInfo } from '@pickleballinc/ui/types/rte';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, /* useRef, */ useState } from 'react';

import { TLTickerScroller } from '@/modules/ticker/TLTickerScroller';
import {
  Matchup,
  TeamLeagueMatchInfo,
  TeamLeagueMatchup,
  TLTickerDataInterface,
} from '@/modules/ticker/types';
import { formatTLTickerMatchups } from '@/utils/helpers/formatTLTickerMatchups';

interface TLTickerProps {
  initialMatches: TLTickerDataInterface;
  teamLeagueUuid: string;
  organizationUuid: string;
}

export function TLTicker({ initialMatches, teamLeagueUuid, organizationUuid }: TLTickerProps) {
  const seasonUuid = initialMatches.seasonUuid;
  const utcOffset = initialMatches.utcOffset;

  const { data } = useInfiniteQuery({
    queryKey: ['tl-ticker', teamLeagueUuid, organizationUuid],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams({
        current_page: `${pageParam}`,
        season_uuid: seasonUuid,
      });
      if (utcOffset) params.set('utc_offset', utcOffset);
      params.set('team_league_uuid', teamLeagueUuid);
      params.set('organization_uuid', organizationUuid);
      const response = await fetch(`/api/v2/tl-ticker?${params.toString()}`);
      const result: { data: TLTickerDataInterface; statusCode: number; error?: string } =
        await response.json();

      if (result.statusCode !== 200 || !result.data || result.data.matchups.length === 0)
        return null;

      return {
        page: pageParam,
        data: result.data,
      };
    },
    initialData: {
      pages: [
        {
          page: 1,
          data: initialMatches,
        },
      ],
      pageParams: [undefined],
    },
    refetchInterval: 3600000, // hard reload ticker matchups every 60 minutes (needed because of midnight)
    refetchOnWindowFocus: false,
  });

  const [matchupsData, setMatchupsData] = useState<TeamLeagueMatchup[]>([]);
  const [filteredMatchups, setFilteredMatchups] = useState<Matchup[]>([]);

  // when all matchups are fetched data is updated, we need to update matchupsData with matchups that aren't in the first up to 5 prefetched matchups
  useMemo(() => {
    setMatchupsData((matchupsPrev) => {
      const matchupsToAdd: TeamLeagueMatchup[] = [];

      // Only add matchups that do not already exist in matchupsPrev
      data?.pages.forEach((page) => {
        page?.data.matchups.forEach((newMatchup) => {
          if (matchupsPrev.findIndex((matchup) => matchup.uuid === newMatchup.uuid) === -1) {
            matchupsToAdd.push(newMatchup);
          }
        });
      });

      // Only update state if there are new matchups to add
      if (matchupsToAdd.length > 0) {
        return [...matchupsPrev, ...matchupsToAdd];
      }

      return matchupsPrev; // If no new matchups, return the current state to prevent re-renders
    });
  }, [data]);

  const { matches } = useRTE();
  // const timeoutMapRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  useEffect(() => {
    setMatchupsData((matchupsPrev) => {
      const matchupsDataCopy = structuredClone(matchupsPrev);

      matches.forEach((newMatch) => {
        let matchupIndex = -1;
        let matchIndex = -1;

        for (let i = 0; i < matchupsDataCopy.length; i++) {
          const matchup = matchupsDataCopy[i];
          if (!matchup) continue;

          const matchIdx = matchup.matches.findIndex(
            (match) => match.matchUuid === newMatch.matchUuid
          );

          if (matchIdx !== -1) {
            matchupIndex = i;
            matchIndex = matchIdx;
            break;
          }
        }

        if (matchIndex !== -1) {
          const matchupContainingMatch = matchupsDataCopy[matchupIndex];
          const updatedMatchup = structuredClone(matchupContainingMatch);
          if (matchupContainingMatch && updatedMatchup) {
            const matchToUpdate = matchupContainingMatch.matches[matchIndex];

            let matchupMoved = false; // Flag to check if the matchup has been moved

            if (matchToUpdate) {
              // Check if at least one field in newMatch is different from matchToUpdate
              const hasDifferences = (Object.keys(newMatch) as (keyof RTEMatchInfo)[]).some(
                (key) => newMatch[key] !== matchToUpdate[key as keyof TeamLeagueMatchInfo]
              );

              // Create a new object by removing any `undefined` values from `newMatch` and adding `changeHappened` only if there are differences
              const filteredNewMatch = Object.fromEntries(
                Object.entries({ ...newMatch, changeHappened: hasDifferences }).filter(
                  ([_, value]) => value !== undefined
                )
              );

              if (hasDifferences) {
                if (
                  !matchToUpdate.localDateMatchAssignedToCourt &&
                  !matchToUpdate.localDateMatchStart &&
                  !matchToUpdate.localDateMatchCompleted &&
                  filteredNewMatch.localDateMatchAssignedToCourt
                ) {
                  // 1. Match just assigned to the court - move its matchup to the first index after all the matchups that are live
                  const lastLiveMatchupIndex = matchupsDataCopy.findLastIndex((matchup) =>
                    matchup.matches.some((match) => match.matchStatus === 2)
                  );

                  const insertIndex = lastLiveMatchupIndex !== -1 ? lastLiveMatchupIndex + 1 : 0;

                  // Remove matchup from its current position
                  matchupsDataCopy.splice(matchupIndex, 1);

                  updatedMatchup.matches[matchIndex] = {
                    ...matchToUpdate,
                    ...filteredNewMatch,
                  } as TeamLeagueMatchInfo;

                  // Insert matchup with updated fields in the correct position
                  matchupsDataCopy.splice(insertIndex, 0, updatedMatchup);

                  matchupMoved = true;
                } else if (matchToUpdate.matchStatus !== 2 && filteredNewMatch.matchStatus === 2) {
                  // 2. Match just started, meaning match is now live - if its parent matchup has only now became live move it after the last live matchup
                  const isMatchupAlreadyLive = matchupContainingMatch.matches.some(
                    (match) => match.matchStatus === 2 || match.matchStatus === 4
                  ); // matchup is live if there's already a match inside it that's live, or if there's a match inside it that has completed

                  if (!isMatchupAlreadyLive) {
                    let insertIndex: number;
                    insertIndex = matchupsDataCopy.findIndex(
                      (matchup) => matchup.matchupStatus !== 'IN_PROGRESS_MATCHUP_STATUS'
                    );
                    insertIndex = insertIndex === -1 ? matchupsDataCopy.length : insertIndex; // If no non-live matchups (all matchups are live), place at the end

                    // Remove the matchup from its current position
                    matchupsDataCopy.splice(matchupIndex, 1);

                    updatedMatchup.matches[matchIndex] = {
                      ...matchToUpdate,
                      ...filteredNewMatch,
                    } as TeamLeagueMatchInfo;

                    // Insert the updated live match at the correct index
                    matchupsDataCopy.splice(insertIndex, 0, {
                      ...updatedMatchup,
                      matchupStatus: 'IN_PROGRESS_MATCHUP_STATUS',
                    });

                    matchupMoved = true;
                  }
                }
                // NOTE:
                // No way for me to determine based on if match completed, that matchup has completed as well,
                // I (Edin) contacted both Kenan and Selsebil about this, they said they can't give me this info on match update through RTE that matchup completed as well...
                // else if (matchToUpdate.matchStatus !== 4 && filteredNewMatch.matchStatus === 4) {
                // 	// Clear any existing timeout for this match to prevent duplicates
                // 	const existingTimeout = timeoutMapRef.current.get(matchToUpdate.matchUuid);
                // 	if (existingTimeout) {
                // 		clearTimeout(existingTimeout);
                // 	}

                // 	// Schedule the match to move to the end of the list after 30 seconds
                // 	const timeout = setTimeout(() => {
                // 		setMatchesData((matchesPrev) => {
                // 			const matchesDataCopy = [...matchesPrev];
                // 			const matchIndex = matchesDataCopy.findIndex((match) => match.matchUuid === newMatch.matchUuid);

                // 			if (matchIndex !== -1) {
                // 				const [completedMatch] = matchesDataCopy.splice(matchIndex, 1);
                // 				matchesDataCopy.push(completedMatch as MatchInfoV2);
                // 			}

                // 			return matchesDataCopy;
                // 		});
                // 		// Remove timeout from map after it executes
                // 		timeoutMapRef.current.delete(matchToUpdate.matchUuid);
                // 	}, SIT_TIME_AFTER_COMPLETION);

                // 	// Add the new timeout to the map
                // 	timeoutMapRef.current.set(matchToUpdate.matchUuid, timeout);
                // }
              }

              // Ensure this update only happens if the matchup wasn’t moved
              if (!matchupMoved) {
                matchupContainingMatch.matches[matchIndex] = {
                  ...matchToUpdate,
                  ...filteredNewMatch,
                } as TeamLeagueMatchInfo;
              }
            }
          }
        }
      });

      return [...matchupsDataCopy];
    });
  }, [matches]);

  useMemo(() => {
    const filteredMatchupsNew = formatTLTickerMatchups({
      matchupsInfos: matchupsData,
    });

    setFilteredMatchups(filteredMatchupsNew);
  }, [matchupsData]);

  if (filteredMatchups.length > 0) {
    return <TLTickerScroller results={filteredMatchups} />;
  }

  return null;
}
